/* eslint-disable camelcase */

import { ref, useRoute, useStore } from '@nuxtjs/composition-api'
import { catchError, empty, from, tap } from 'rxjs'
import useUser from './useUser'
import useLocale from './useLocale'

export default function useCSR(context) {
  const route = useRoute()
  const store = useStore()

  const { user } = useUser(context)
  const { locale } = useLocale()

  //
  // liveChat
  //
  function initLivechatCSRWidget(
    { isUserVariablesSetting = false, isMinimizedThenHideWindow = true } = {
      isUserVariablesSetting: false,
      isMinimizedThenHideWindow: true
    }
  ) {
    let LC_API

    const init = () => {
      LC_API = window.LC_API

      if (!LC_API) return

      let livechat_chat_started = false

      LC_API.on_after_load = () => {
        // don't hide the chat window only if visitor
        // is currently chatting with an agent
        if (
          LC_API.visitor_engaged() === false &&
          livechat_chat_started === false
        ) {
          LC_API.hide_chat_window()
        }

        if (isMinimizedThenHideWindow) {
          LC_API.on_chat_window_minimized = () => {
            LC_API.hide_chat_window()
          }
        }

        isUserVariablesSetting && submitLivechatUserVariables()

        if (route.value.query?.openCSRWidget === 'true') {
          openLivechatCSRWidget()
        }
      }

      LC_API.on_chat_started = () => {
        livechat_chat_started = true
      }
    }

    init()

    let pollingTime = 1

    const intervalId = setInterval(() => {
      if (
        ++pollingTime > 30 ||
        (LC_API &&
          LC_API.hasOwnProperty('is_loaded') &&
          LC_API.hasOwnProperty('on_after_load') &&
          LC_API.hasOwnProperty('on_chat_started'))
      ) {
        clearInterval(intervalId)
        return
      }

      console.log(`LC_API${pollingTime ? ` (${pollingTime})` : ''}:`, LC_API)
      init()
    }, 100)
  }
  const isLivechatUserVariablesSetting = ref(false)
  function submitLivechatUserVariables() {
    if (window.LC_API && user.value) {
      const custom_variables = [
        { name: 'user_id', value: user.value.username },
        { name: 'user_level', value: user.value.level },
        { name: 'user_bet', value: user.value.userTotalBet.sumOfValidBet },
        { name: 'user_ngr', value: user.value.userTotalBet.sumOfNgrFromStart },
        { name: 'user_register_time', value: user.value.registerTime }
      ]
      if (!isLivechatUserVariablesSetting.value) {
        if (window.LC_API.hasOwnProperty('set_custom_variables')) {
          window.LC_API.set_custom_variables(custom_variables)
          isLivechatUserVariablesSetting.value = true
        }
      } else if (window.LC_API.hasOwnProperty('update_custom_variables')) {
        window.LC_API.update_custom_variables(custom_variables)
      }
    }
  }
  function clearLivechatUserVariables() {
    if (
      isLivechatUserVariablesSetting.value &&
      window.LC_API.hasOwnProperty('update_custom_variables')
    ) {
      window.LC_API.update_custom_variables([
        { name: 'user_id', value: null },
        { name: 'user_level', value: null },
        { name: 'user_bet', value: null },
        { name: 'user_ngr', value: null },
        { name: 'user_register_time', value: null }
      ])
    }
  }
  function openLivechatCSRWidget() {
    if (window.LC_API.open_chat_window) {
      window.LC_API.open_chat_window()
    }
  }

  //
  // zendesk
  //
  function initZendeskCSRWidget() {
    if (window.zE) {
      window.zE('messenger:set', 'locale', locale.value || 'en')
    }
  }
  function loginZendeskCSRWidget() {
    from(store.dispatch('authModule/getZendeskJWTForToken'))
      .pipe(
        tap(data => {
          if (window.zE && data) {
            window.zE('messenger', 'loginUser', function(callback) {
              callback(data)
            })
          }
        }),
        catchError(err => {
          console.error(err)
          return empty()
        })
      )
      .subscribe()
  }
  function logoutZendeskCSRWidget() {
    if (window.zE) {
      window.zE('messenger', 'logoutUser')
    }
  }
  function openZendeskCSRWidget() {
    if (window.zE) {
      window.zE('messenger', 'open')
    }
  }

  //
  // tawk
  //
  function initTawkCSRWidget() {
    if (window.Tawk_API) {
      window.Tawk_API.onChatMinimized = () => {
        window.Tawk_API.hideWidget()
      }
      window.Tawk_API.onLoad = () => {
        window.Tawk_API.hideWidget()
      }
      return true
    }
    return false
  }
  function openTawkCSRWidget() {
    if (window.Tawk_API) {
      window.Tawk_API.showWidget()
      window.Tawk_API.maximize()
    }
  }

  //
  // zoho
  //
  function initZohoCSRWidget() {
    if (window.$zoho) {
      window.$zoho.salesiq.afterReady = function(visitorgeoinfo) {
        console.log(
          'window.$zoho => ratingtitle',
          window.$zoho.salesiq.floatbutton,
          window.$zoho.salesiq.chat
        )

        // set customized messages
        if (window.$zoho.salesiq.chat) {
          window.$zoho.salesiq.chat.systemmessages({
            ratingtitle:
              'サイトに対する要望やチャット対応について評価をお願いいたします。'
          })
        }

        // hide button at page loaded
        window.$zoho.salesiq.floatbutton.visible('hide')

        window.$zoho.salesiq.chatbutton.click(function() {
          console.log('zoho chatbutton clicked')
        })

        window.$zoho.salesiq.floatwindow.minimize(function() {
          console.log('floatwindow minimize')
          window.$zoho.salesiq.floatbutton.visible('hide')
        })

        window.$zoho.salesiq.visitor.missed(function(visitid, data) {
          console.log('SalesIQ: visitor.missed', visitid, data)
          // if (context.root.$sentry) {
          //   context.root.$sentry.captureMessage(
          //     JSON.stringify({
          //       title: 'SalesIQ: visitor.missed',
          //       visitid,
          //       data
          //     }),
          //     'info'
          //   )
          // }
        })

        window.$zoho.salesiq.visitor.chat(function(visitid, data) {
          console.log('SalesIQ: visitor.chat', visitid, data)
          // if (context.root.$sentry) {
          //   context.root.$sentry.captureMessage(
          //     JSON.stringify({
          //       title: 'SalesIQ: visitor.chat',
          //       visitid,
          //       data
          //     }),
          //     'info'
          //   )
          // }
        })
      }
      return true
    }
    return false
  }
  function openZohoCSRWidget() {
    if (window.$zoho) {
      window.$zoho.salesiq.floatwindow.visible('show')
    }
  }

  //
  // ps
  //
  function openPSCSRWidget() {
    if (window.psOpenWindow) {
      window.psOpenWindow()
    }
  }

  //
  // D8 Chat
  //
  /**
   *
   * @param selector
   * @param userIdentity userIdentity 是一個測試用的使用者身分，用來判斷使用者身分的唯一值。這個值必須和之後聊天室向你們後端打API取得個人資料時的身分值相同
   * @param domain
   */
  function openD8ChatCSRWidget(
    selector = '#chat',
    userIdentity = null,
    domain
  ) {
    if (window.d8Chat) {
      window.d8Chat.init(selector, userIdentity, {
        domain,
        showMenu: true
      })
    }
  }

  return {
    initLivechatCSRWidget,
    submitLivechatUserVariables,
    clearLivechatUserVariables,
    openLivechatCSRWidget,

    initZendeskCSRWidget,
    loginZendeskCSRWidget,
    logoutZendeskCSRWidget,
    openZendeskCSRWidget,

    initTawkCSRWidget,
    openTawkCSRWidget,

    initZohoCSRWidget,
    openZohoCSRWidget,

    openPSCSRWidget,

    openD8ChatCSRWidget
  }
}
